import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hook";
import { useAddProductCartMutation, useGetCartByUserQuery } from "../utils/api/product/product.api";
import { CartItemType, addCartProduct } from "../redux/slice/useCartSlice";

/**
 * Hook pour synchroniser le panier localStorage avec le backend
 * 
 * Comportement :
 * - Si utilisateur NON connecté : panier uniquement dans localStorage (via Redux Persist)
 * - Si utilisateur connecté : 
 *   1. À la connexion : fusionne le panier localStorage avec le panier backend
 *   2. Pendant l'utilisation : synchronise chaque modification vers le backend
 */
export const useCartSync = () => {
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((s) => s?.user);
  const { cartItems } = useAppSelector((state) => state.cart);
  const [addProductCart] = useAddProductCartMutation();
  const { data: backendCart, refetch: refetchBackendCart } = useGetCartByUserQuery(
    user?.slug || "",
    { skip: !user?.slug } // Skip si pas connecté
  );
  
  // Références pour éviter les boucles infinies
  const hasSyncedOnLoginRef = useRef(false);
  const isSyncingRef = useRef(false);
  const lastCartItemsLengthRef = useRef(cartItems.length);
  const lastCartItemsHashRef = useRef("");

  /**
   * Génère un hash des cartItems pour détecter les changements
   */
  const getCartItemsHash = (items: CartItemType[]) => {
    return JSON.stringify(
      items.map(item => ({
        id: item.produit?.id,
        varId: item.variations?.[0]?.id || 'no-var',
        qty: item.quantite
      }))
    );
  };

  /**
   * Synchronise un produit du panier localStorage vers le backend
   */
  const syncItemToBackend = async (item: CartItemType) => {
    if (!user?.slug || !token || isSyncingRef.current) return;

    try {
      await addProductCart({
        produit: parseInt(item.produit.id, 10),
        quantite: item.quantite,
        prix: item.prix,
        variations: item.variations,
      }).unwrap();
    } catch (error) {
      console.error("Erreur lors de la synchronisation du panier:", error);
    }
  };

  /**
   * Fusionne le panier localStorage avec le panier backend lors de la connexion
   */
  useEffect(() => {
    if (user?.slug && token && backendCart && !hasSyncedOnLoginRef.current) {
      hasSyncedOnLoginRef.current = true;
      isSyncingRef.current = true;

      const localCartItems = cartItems || [];
      const backendCartItems = backendCart?.items || [];

      // Si le panier backend est vide, on synchronise tout le localStorage
      if (backendCartItems.length === 0 && localCartItems.length > 0) {
        localCartItems.forEach((localItem: CartItemType) => {
          syncItemToBackend(localItem);
        });
      } 
      // Si le panier localStorage est vide, on charge le panier backend dans Redux
      else if (localCartItems.length === 0 && backendCartItems.length > 0) {
        backendCartItems.forEach((backendItem: any) => {
          dispatch(addCartProduct({
            produit: backendItem.produit,
            prix: backendItem.prix_total / backendItem.quantite,
            quantite: backendItem.quantite,
            stock: backendItem.produit?.stock || 0,
            variations: backendItem.variations || [],
            vendeur: backendItem.produit?.vendeur?.id || 0,
          }));
        });
      }
      // Si les deux ont des produits, on fusionne intelligemment
      else if (localCartItems.length > 0 && backendCartItems.length > 0) {
        // Créer un Map des produits backend
        const backendItemsMap = new Map();
        backendCartItems.forEach((item: any) => {
          const key = `${item.produit?.id}_${item.variations?.[0]?.id || 'no-var'}`;
          backendItemsMap.set(key, item);
        });

        // Synchroniser chaque produit du localStorage vers le backend
        localCartItems.forEach((localItem: CartItemType) => {
          const key = `${localItem.produit?.id}_${localItem.variations?.[0]?.id || 'no-var'}`;
          const backendItem = backendItemsMap.get(key);

          if (!backendItem) {
            // Le produit n'existe pas dans le backend, on l'ajoute
            syncItemToBackend(localItem);
          } else if (localItem.quantite !== backendItem.quantite) {
            // La quantité diffère, on met à jour avec la quantité du localStorage
            syncItemToBackend(localItem);
          }
        });
      }

      // Après fusion, recharger le panier depuis le backend
      setTimeout(() => {
        refetchBackendCart();
        isSyncingRef.current = false;
      }, 1500);
    }
  }, [user?.slug, token, backendCart]);

  /**
   * Réinitialise les flags lors de la déconnexion
   */
  useEffect(() => {
    if (!user?.slug) {
      hasSyncedOnLoginRef.current = false;
      isSyncingRef.current = false;
      lastCartItemsLengthRef.current = 0;
      lastCartItemsHashRef.current = "";
    }
  }, [user?.slug]);

  /**
   * Synchronise automatiquement chaque modification du panier vers le backend
   * (si l'utilisateur est connecté et après la synchronisation initiale)
   */
  useEffect(() => {
    if (!user?.slug || !token || !hasSyncedOnLoginRef.current || isSyncingRef.current) {
      return;
    }

    const currentHash = getCartItemsHash(cartItems);
    const hasChanged = 
      cartItems.length !== lastCartItemsLengthRef.current ||
      currentHash !== lastCartItemsHashRef.current;

    if (hasChanged) {
      // Synchroniser tous les produits du panier vers le backend
      cartItems.forEach((item: CartItemType) => {
        syncItemToBackend(item);
      });

      lastCartItemsLengthRef.current = cartItems.length;
      lastCartItemsHashRef.current = currentHash;
    }
  }, [cartItems, user?.slug, token]);

  return {
    isSyncing: isSyncingRef.current,
    hasSynced: hasSyncedOnLoginRef.current,
  };
};

